export const OPENVAS_CONNECTOR_FIELD_MAX = 256;
export const ZAP_CONNECTOR_FIELD_MAX = 256;
export const CONNECTOR_SEARCH_MAX_LEN = 500;
export const ASSIGNMENT_SEARCH_RESULT_LIMIT = 8;

export function sanitizeConnectorField(raw, maxLen = OPENVAS_CONNECTOR_FIELD_MAX) {
  return String(raw ?? '')
    .replace(/\0/g, '')
    .slice(0, maxLen);
}

export function sanitizeOpenvasConnectorField(raw) {
  return sanitizeConnectorField(raw, OPENVAS_CONNECTOR_FIELD_MAX);
}

export function sanitizeZapConnectorField(raw) {
  return sanitizeConnectorField(raw, ZAP_CONNECTOR_FIELD_MAX);
}

export function sanitizeConnectorSearchInput(raw) {
  return sanitizeConnectorField(raw, CONNECTOR_SEARCH_MAX_LEN);
}

export function getOpenvasConnectorInitialState() {
  return {
    name: '',
    vanityName: '',
    host: '',
    port: '',
    gvmUser: '',
    gvmPassword: '',
    sshUser: '',
    sshPassword: '',
    perimeterScanner: false,
    enabled: false,
  };
}

export function getZapConnectorInitialState() {
  return {
    name: '',
    rportClient: '',
    apiKey: '',
    apiKeySet: false,
    port: '',
    sshUser: '',
    sshPassword: '',
    sshPasswordSet: false,
    poolScope: 'PUBLIC',
    dedicatedOrg: '',
    enabled: false,
  };
}

export function displayConnectorName(row) {
  const vanity = String(row?.vanityName || row?.name || '').trim();
  const technical = String(row?.technicalName || '').trim();
  if (vanity && technical && vanity !== technical) {
    return { primary: vanity, secondary: technical };
  }
  return { primary: vanity || 'Unnamed connector', secondary: '' };
}

export function formatAssignmentSummary(assignments, emptyLabel = 'Unassigned') {
  const names = (Array.isArray(assignments) ? assignments : [])
    .map((item) => String(item?.site || item?.name || item?.org || '').trim())
    .filter(Boolean);
  if (names.length === 0) return emptyLabel;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  return `${names[0]} +${names.length - 1} more`;
}

export function siteLocationLabels(site) {
  const plan = String(site?.plan_name || site?.stripe_plan_name || '').toLowerCase();
  const labels = [];
  if (plan.includes('on-prem') || plan.includes('onprem') || plan.includes('on prem')) labels.push('On-premise');
  if (plan.includes('perimeter')) labels.push('Perimeter');
  if (labels.length) return labels;
  const onprem = Number(site?.licensed_num_essentials_network_onprem || 0)
    + Number(site?.licensed_num_enterprise_network_onprem || 0);
  const perimeter = Number(site?.licensed_num_essentials_network_perimeter || 0)
    + Number(site?.licensed_num_enterprise_network_perimeter || 0);
  if (onprem) labels.push('On-premise');
  if (perimeter) labels.push('Perimeter');
  return labels;
}

export function formatConnectorLocation(assignments, { perimeterScanner = null, emptyLabel = '—' } = {}) {
  const labels = new Set();
  if (perimeterScanner === true) labels.add('Perimeter');
  (Array.isArray(assignments) ? assignments : []).forEach((item) => {
    const raw = item?.locations || item?.location;
    const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    list.forEach((value) => {
      const text = String(value || '').trim();
      const normalized = text.toLowerCase().replace(/[-\s]/g, '');
      if (normalized === 'onprem' || normalized === 'onpremise' || normalized === 'onpremises') {
        labels.add('On-premise');
      }
      if (text === 'Perimeter') labels.add('Perimeter');
    });
  });
  const ordered = ['On-premise', 'Perimeter'].filter((label) => labels.has(label));
  return ordered.length ? ordered.join(', ') : emptyLabel;
}

export function groupConnectorOrgSites(assignments) {
  const order = [];
  const byOrg = new Map();
  (Array.isArray(assignments) ? assignments : []).forEach((item) => {
    const site = String(item?.site || '').trim();
    const org = String(item?.org || '').trim();
    const orgLabel = org && org !== 'Unknown organization' ? org : '';
    if (!site && !orgLabel) return;
    const key = orgLabel || `__site__:${site}`;
    if (!byOrg.has(key)) {
      byOrg.set(key, { org: orgLabel, sites: [], siteSet: new Set() });
      order.push(key);
    }
    const group = byOrg.get(key);
    if (site && !group.siteSet.has(site)) {
      group.siteSet.add(site);
      group.sites.push(site);
    }
  });
  return order.map((key) => {
    const group = byOrg.get(key);
    return { org: group.org, sites: group.sites };
  });
}

export function listConnectorOrgSites(assignments) {
  const lines = [];
  const seen = new Set();
  groupConnectorOrgSites(assignments).forEach((group) => {
    const orgLabel = String(group?.org || '').trim();
    const sites = Array.isArray(group?.sites) ? group.sites : [];
    if (sites.length === 0) {
      if (!orgLabel) return;
      if (seen.has(orgLabel)) return;
      seen.add(orgLabel);
      lines.push(orgLabel);
      return;
    }
    sites.forEach((site) => {
      const line = orgLabel ? `${orgLabel} · ${site}` : site;
      if (seen.has(line)) return;
      seen.add(line);
      lines.push(line);
    });
  });
  return lines;
}

export function formatConnectorOrgSites(assignments, emptyLabel = '—') {
  const lines = listConnectorOrgSites(assignments);
  if (lines.length === 0) return emptyLabel;
  if (lines.length === 1) return lines[0];
  if (lines.length === 2) return lines.join(', ');
  return `${lines[0]} +${lines.length - 1} more`;
}

export function filterConnectorsByName(rows, query) {
  const q = String(query || '').trim().toLowerCase();
  const list = Array.isArray(rows) ? rows : [];
  if (!q) return list;
  return list.filter((row) => {
    const name = String(row?.name || '').toLowerCase();
    const vanity = String(row?.vanityName || '').toLowerCase();
    const technical = String(row?.technicalName || '').toLowerCase();
    const assignment = String(row?.assignmentSummary || '').toLowerCase();
    const orgSite = String(row?.orgSite || '').toLowerCase();
    const orgSites = Array.isArray(row?.orgSites) ? row.orgSites.join(' ').toLowerCase() : '';
    return name.includes(q) || vanity.includes(q) || technical.includes(q) || assignment.includes(q) || orgSite.includes(q) || orgSites.includes(q);
  });
}

function collectEmailValues(source) {
  if (!source || typeof source !== 'object') return [];
  const keys = [
    'email',
    'user_email',
    'owner_email',
    'contact_email',
    'created_by_email',
    'organization_email',
    'billing_email',
  ];
  const values = keys.map((key) => String(source[key] || '').trim().toLowerCase()).filter(Boolean);
  const nested = [source.created_by, source.owner, source.organization, source.user]
    .filter((item) => item && typeof item === 'object')
    .flatMap((item) => collectEmailValues(item));
  return Array.from(new Set([...values, ...nested]));
}

export function inferAssignmentKind(site, perimeterScanner = false) {
  const plan = String(site?.stripe_plan_name || site?.plan_name || site?.scan_type || '').toLowerCase();
  if (plan.includes('perimeter')) return 'Perimeter';
  if (plan.includes('on-prem') || plan.includes('on prem') || plan.includes('onpremise')) return 'On Premise';
  return perimeterScanner ? 'Perimeter' : 'On Premise';
}

export function normalizeAssignmentOption(site, extras = {}) {
  const id = String(site?.uu_id || site?.site_uuid || site?.id || extras.id || '').trim();
  const siteName = String(site?.site_name || site?.name || extras.site || '').trim() || id;
  const orgName = String(
    site?.organization_name ||
      site?.organization ||
      site?.org_name ||
      site?.organization?.name ||
      extras.org ||
      ''
  ).trim();
  const orgId = String(
    site?.organization_id ||
      site?.organization_uuid ||
      site?.org_id ||
      site?.organization?.uu_id ||
      site?.organization?.id ||
      extras.orgId ||
      ''
  ).trim();
  const emails = Array.from(
    new Set([...(extras.emails || []), ...collectEmailValues(site), ...collectEmailValues(extras)])
  );
  return {
    id,
    org: orgName || 'Unknown organization',
    orgId,
    site: siteName || 'Unnamed site',
    emails,
    kind: extras.kind || inferAssignmentKind(site, extras.perimeterScanner),
    locations: Array.isArray(extras.locations)
      ? extras.locations
      : (Array.isArray(site?.locations) ? site.locations : []),
  };
}

export function assignmentSearchHaystack(option) {
  return [option?.org, option?.site, option?.kind, ...(option?.emails || [])]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
}

export function matchAssignmentQuery(option, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return false;
  return assignmentSearchHaystack(option).includes(q);
}

export function filterAssignmentSearchResults(options, query, selectedIds, limit = ASSIGNMENT_SEARCH_RESULT_LIMIT) {
  const selected = new Set((selectedIds || []).map((id) => String(id)));
  return (Array.isArray(options) ? options : [])
    .filter((option) => option?.id && !selected.has(String(option.id)))
    .filter((option) => matchAssignmentQuery(option, query))
    .slice(0, limit);
}

export function mapAssignedSitesFromConnector(row) {
  const assigned = Array.isArray(row?.assigned_sites) ? row.assigned_sites : [];
  return assigned
    .map((site) =>
      normalizeAssignmentOption(site, {
        id: site?.uu_id || site?.id,
        site: site?.name || site?.site_name,
        org: site?.organization_name || (typeof site?.organization === 'string' ? site.organization : '') || site?.org,
        locations: site?.locations,
      })
    )
    .filter((item) => item.id);
}

export function emptyDisplayValue(value, fallback = '—') {
  const text = String(value ?? '').trim();
  return text || fallback;
}

export function isPermissionDeniedError(err) {
  const text = String(err?.message || '').toLowerCase();
  return (
    text.includes('403') ||
    text.includes('forbidden') ||
    text.includes('permission denied') ||
    text.includes('not permitted')
  );
}
